import { useCallback, useEffect, useState } from 'react';

// Hooks
import useFetchIntervalData from '../useFetchIntervalData';
import useSubscriptionClick from '../useSubscriptionClick';

// Utils
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { getMultiSportEvent } from '../../services/multi-sport/events';
import { normalizeEventResponseData } from '../../normalizers/src/multi-sport/normalizeEventResponseData';

// Types
import { IMultiSportConfig, INormalizedGame } from '../../types/multisport';
import { SDKOptions } from '../../types/common';
import { IGeneralSubscriberInfo } from '../../constants/subscribeTypes';
import { IMultiSportEventResponse } from '../../types';

interface IUseEventArgs extends IGeneralSubscriberInfo {
  dataRefreshTime?: IMultiSportConfig['dataRefreshTime'];
  dataOddsDisplay: IMultiSportConfig['dataOddsDisplay'];
  dataOddsMarketValueType: IMultiSportConfig['dataOddsMarketValueType'];
  dataOddsBettingId: IMultiSportConfig['dataOddsBettingId'];
  dataOddsMarket: IMultiSportConfig['dataOddsMarket'];
  dataOddsPreEventOnly: IMultiSportConfig['dataOddsPreEventOnly'];
  dataShortStatusType: IMultiSportConfig['dataShortStatusType'];
  dataTeamNameType: IMultiSportConfig['dataTeamNameType'];
  dataEntityLinks: IMultiSportConfig['dataEntityLinks'];
  sdkOptions: SDKOptions;
  idOrSlug?: string;
  sport: string;
}

export const useEvent = ({
  subscribers,
  dataRefreshTime,
  dataWidgetId,
  dataWidgetSport,
  dataOddsDisplay,
  dataOddsMarketValueType,
  dataOddsPreEventOnly,
  dataOddsBettingId,
  dataOddsMarket,
  dataWidgetCuid,
  sdkOptions,
  idOrSlug,
  dataShortStatusType,
  dataTeamNameType,
  dataEntityLinks,
  sport,
}: IUseEventArgs) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [game, setGame] = useState<INormalizedGame | undefined>();

  const params = {
    idOrSlug,
    sport,
    dataOddsDisplay: dataOddsDisplay,
    dataOddsBettingId: dataOddsBettingId,
    dataOddsMarket: dataOddsMarket,
    dataOddsMarketValueType: dataOddsMarketValueType,
    dataOddsPreEventOnly: dataOddsPreEventOnly,
  };

  const fetchGame = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      manageLoader && setIsLoading(true);
      getMultiSportEvent({ params, sdkOptions })?.then((event: IMultiSportEventResponse) => {
        manageLoader && setIsLoading(false);
        const game = normalizeEventResponseData({
          game: event,
          sdkOptions,
          dataShortStatusType,
          dataTeamNameType,
          dataEntityLinks,
        });
        setGame(game);
        !isFromInterval && stackOdds(event, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId, true);
      });
    },
    [
      dataOddsBettingId,
      dataOddsDisplay,
      dataOddsMarket,
      dataOddsMarketValueType,
      dataOddsPreEventOnly,
      idOrSlug,
      dataWidgetCuid,
      dataWidgetId,
      dataWidgetSport,
    ],
  );

  useFetchIntervalData(fetchGame, dataRefreshTime);

  useEffect(() => {
    fetchGame(true);
  }, [fetchGame]);

  return {
    widgetData: { game, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
};
